import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withStyles } from '@material-ui/core/styles';
import Popover from '@material-ui/core/Popover';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import HTMLElementType from '@material-ui/utils/HTMLElementType';
import Routes from 'Routes';
import { PAGE_NAMES } from 'appenv';

import { useSelector } from 'react-redux';
import { UserSessionContext } from 'components/UserSession';
import { selectExhibitorsByKeyword } from 'models/exhibitors';
import { selectWebinarsByKeyword } from 'models/webinars';

import SearchResultItem from './SearchResultItem';

const StyledPopover = withStyles(() => ({
  paper: {
    width: '400px',
    maxHeight: '300px',
  },
}))(Popover);

const StyledSubHeader = styled.div`
  font-style: normal;
  font-weight: normal;
  font-size: 11px;
  line-height: 11px;
  color: #A2AAB3;
  margin: 8px 16px;
`;

const EmptyState = styled.div`
  color: #A2AAB3;
  padding: 8px 16px;
`;

const ViewAllResultsItem = withStyles(() => ({
  root: {
    fontFamily: 'Lato',
    fontStyle: 'normal',
    fontWeight: 'normal',
    fontSize: '14px',
    color: '#484848',
    borderTop: '1px solid #DBDEE2',
    textAlign: 'center',
    paddingTop: '13px',
    paddingBottom: '13px',
    marginTop: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))(MenuItem);

const SearchResultList = ({
  anchorEl,
  open,
  onClose,
  onExited,
  searchText,
  navigateToAllResults,
}) => {
  const locale = 'en';
  const exhibitors = useSelector(selectExhibitorsByKeyword(searchText));
  const webinars = useSelector(selectWebinarsByKeyword(searchText));
  const { canSee } = useContext(UserSessionContext);

  const canSeeExhibitors = canSee('hall');
  const showExhibitors = exhibitors.length > 0 && canSeeExhibitors;
  const canSeeWebinars = canSee('webinar');
  const showWebinars = webinars.length > 0 && canSeeWebinars;

  return (
    <StyledPopover
      id="search-result-list"
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      onExited={onExited}
      getContentAnchorEl={null}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      disableEnforceFocus
      disableAutoFocus
    >
      <MenuList>
        {
          canSeeExhibitors && (
            <StyledSubHeader>
              {PAGE_NAMES.exhibitors}
            </StyledSubHeader>
          )
        }
        {
            showExhibitors ? (
              exhibitors.map((exhibitor) => {
                const { name } = exhibitor.translations[locale] || {};

                return (
                  <SearchResultItem
                    key={exhibitor.id}
                    to={Routes.booth(exhibitor.id)}
                    searchText={searchText}
                    resultText={name}
                  />
                );
              })
            ) : (
              canSeeExhibitors && (
                <EmptyState>No Results</EmptyState>
              )
            )
          }
        {
          canSeeWebinars && (
            <StyledSubHeader>
              {PAGE_NAMES.webinar}
            </StyledSubHeader>
          )
        }
        {
            showWebinars ? (
              webinars.map((webinar) => {
                const { title } = webinar.translations[locale] || {};
                return (
                  <SearchResultItem
                    key={webinar.id}
                    to={Routes.webinar}
                    searchText={searchText}
                    resultText={title}
                  />
                );
              })
            ) : (
              canSeeWebinars && <EmptyState>No Results</EmptyState>
            )
          }
      </MenuList>
      {
        (showExhibitors || showWebinars) ? (
          <ViewAllResultsItem onClick={navigateToAllResults}>
            View all search results
          </ViewAllResultsItem>
        ) : null
      }
    </StyledPopover>
  );
};

SearchResultList.propTypes = {
  anchorEl: PropTypes.oneOfType([HTMLElementType, PropTypes.func]),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onExited: PropTypes.func,
  searchText: PropTypes.string,
  navigateToAllResults: PropTypes.func,
};

SearchResultList.defaultProps = {
  anchorEl: null,
  open: false,
  onClose: () => {},
  onExited: () => {},
  searchText: '',
  navigateToAllResults: () => {},
};

export default SearchResultList;
